import { observer } from 'mobx-react-lite';
import { OWA_AUTO_LINKS_CLASSNAME } from 'owa-attachment-link-common';
import { DATA_OG_STYLE_BACKGROUNDCOLOR } from 'owa-content-colors-constants';
import { lazyOperateContent, OperateContentType } from 'owa-editor';
import EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { getCloudCDNIcon } from 'owa-file-icon';
import { getAnchorElementId, RecipientContainer, SharingLinkInfo } from 'owa-link-data';
import * as trace from 'owa-trace';
import React, { CSSProperties } from 'react';
import * as ReactDOM from 'react-dom';
import { insertEntity } from 'roosterjs-editor-api';
import { Position } from 'roosterjs-editor-dom';
import { PositionType } from 'roosterjs-editor-types';
import getComposeLinkViewState from '../selectors/getComposeLinkViewState';
import type ComposeLinkViewState from '../store/schema/ComposeLinkViewState';
import {
    ATTRIBUTE_CONTENTEDITABLE_NAME,
    CLICK_EVENT_NAME,
    CONTEXT_MENU_EVENT_NAME,
    LINK_BACKGROUND_COLOR,
} from '../utils/constants';
import { getIsDarkMode } from '../utils/getIsDarkMode';
import { showLinkContextMenu } from './LinkContextMenu';

interface ThumbnailViewLinkProps {
    sharingLink: SharingLinkInfo;
    operateContent: OperateContentType;
    editorViewState: EditorViewState;
    linkViewState: ComposeLinkViewState;
    composeId: string;
    getRecipientWells: () => RecipientContainer[];
    fromAddress: string;
    originalElement: HTMLElement;
    shouldBeautify: boolean;
    reactContainer: HTMLElement;
    linksContainerId: string; // Is used only in composeScenario
}

const THUMBNAIL_VIEW_LINK_ENTITY_TYPE = 'THUMBNAIL_VIEW_LINK';

export const ThumbnailViewLink = observer(function ThumbnailViewLink(
    props: ThumbnailViewLinkProps
) {
    const linkRef = React.useRef<HTMLAnchorElement>();

    const onClickOrContextMenu = (event: MouseEvent) => {
        showLinkContextMenu(
            linkRef.current,
            props.editorViewState,
            props.operateContent,
            props.composeId,
            props.getRecipientWells(),
            props.fromAddress,
            props.linksContainerId
        );
        event.preventDefault();
    };

    React.useEffect((): React.EffectCallback | void => {
        const link: HTMLSpanElement | null = linkRef.current;

        link.setAttribute(ATTRIBUTE_CONTENTEDITABLE_NAME, 'false');
        link.addEventListener(CLICK_EVENT_NAME, onClickOrContextMenu);
        link.addEventListener(CONTEXT_MENU_EVENT_NAME, onClickOrContextMenu);
        if (getIsDarkMode(props.editorViewState, null)) {
            link.setAttribute(DATA_OG_STYLE_BACKGROUNDCOLOR, LINK_BACKGROUND_COLOR);
        }

        props.operateContent(props.editorViewState, (editor, range, domUtils) => {
            trace.trace.info('Editor insert ThumbnailViewLink');
            const replaceTargets: HTMLElement[] = editor.queryElements(
                '#' + props.originalElement.id
            );
            if (replaceTargets && replaceTargets.length === 1) {
                trace.trace.info('operateContent replacing node with thumbnail view link');

                // Normalize the node to merge any continuous text node and remove empty text node to make sure selection path is correct
                replaceTargets[0].parentNode.normalize();

                const path = editor.getSelectionPath();

                // We need to replace the child only so we keep any text typed under the parent element before the new anchor
                editor.replaceNode(replaceTargets[0], props.reactContainer);

                if (props.shouldBeautify) {
                    const entity = insertEntity(
                        editor,
                        THUMBNAIL_VIEW_LINK_ENTITY_TYPE,
                        props.reactContainer,
                        false /*isBlock*/,
                        true /* isReadOnly */,
                        new Position(props.reactContainer, PositionType.End)
                    );

                    // Normalize the node again to make sure DOM structure is in consistent with the selection path
                    entity?.wrapper?.parentNode?.normalize();
                }

                // Selection path is just some number, which is the index of the selected node.
                // So this can work only when the count of nodes is not changed
                // Since this component always replace one node with another node, the count doesn't change.
                editor.select(path);
            }

            return null;
        });
        return () => {
            if (link) {
                link.removeEventListener(CLICK_EVENT_NAME, onClickOrContextMenu);
                link.removeEventListener(CONTEXT_MENU_EVENT_NAME, onClickOrContextMenu);
            }
        };
    }, []);

    const linkId = getAnchorElementId(props.sharingLink.linkId, props.sharingLink.permissionLevel);
    return (
        <a
            ref={linkRef}
            href={props.sharingLink.url}
            id={linkId}
            target={'_blank'}
            style={getContainerStyle()}
            className={OWA_AUTO_LINKS_CLASSNAME}>
            <div style={getThumbnailContainerStyle()}>
                <img
                    src={props.sharingLink.thumbnailUrl}
                    alt=""
                    role="presentation"
                    style={getThumbnailStyle()}
                />
            </div>
            <div style={getInfoStyle()}>
                <img
                    src={getCloudCDNIcon()}
                    style={getFileIconStyle()}
                    alt=""
                    role="presentation"
                />
                <span spellCheck={false} style={getDisplayNameStyle()}>
                    {props.linkViewState.displayName}
                </span>
            </div>
        </a>
    );
});

function getContainerStyle(): CSSProperties {
    return {
        width: '158px',
        height: '138px',
        backgroundColor: LINK_BACKGROUND_COLOR,
        borderRadius: '3px',
        display: 'block',
        marginRight: '10px',
        position: 'relative',
        top: '5px',
        border: '1px solid ' + LINK_BACKGROUND_COLOR,
    };
}

function getDisplayNameStyle(): CSSProperties {
    return {
        padding: '0 2px 0 1px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '14px',
        fontFamily: 'Segoe UI',
        width: '138px',
        maxWidth: '138px',
        display: 'inline-block',
        textDecoration: 'underline',
    };
}

function getInfoStyle(): CSSProperties {
    return {
        height: '20px',
    };
}

function getFileIconStyle(): CSSProperties {
    return {
        paddingLeft: '1px',
    };
}

function getThumbnailContainerStyle(): CSSProperties {
    return {
        width: '158px',
        height: '118px',
        borderRadius: '3px',
    };
}
function getThumbnailStyle(): CSSProperties {
    return {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '3px',
    };
}

export async function createThumbnailViewLink(
    editorViewState: EditorViewState,
    sharingLink: SharingLinkInfo,
    composeId: string,
    getRecipientWells: () => RecipientContainer[],
    fromAddress: string,
    originalElement: HTMLElement,
    shouldBeautify: boolean,
    linksContainerId: string
) {
    const linkContainer = document.createElement('span');
    const operateContent = await lazyOperateContent.import();
    ReactDOM.render(
        <React.StrictMode>
            <ThumbnailViewLink
                editorViewState={editorViewState}
                linkViewState={getComposeLinkViewState(sharingLink.linkId)}
                sharingLink={sharingLink}
                operateContent={operateContent}
                composeId={composeId}
                getRecipientWells={getRecipientWells}
                fromAddress={fromAddress}
                originalElement={originalElement}
                shouldBeautify={shouldBeautify}
                reactContainer={linkContainer}
                linksContainerId={linksContainerId}
            />
        </React.StrictMode>,
        linkContainer
    );
}
